import {Dialog} from '@primer/react/experimental'
import {
  type PreloadedQuery,
  useQueryLoader,
  useRelayEnvironment,
  graphql,
  usePreloadedQuery,
  useFragment,
} from 'react-relay'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {commitConvertIssueToDiscussionMutation} from '../mutations/convert-issue-to-discussion-mutation'
import type {ConvertIssueToDiscussionInput} from '../mutations/__generated__/convertIssueToDiscussionMutation.graphql'
import {BUTTON_LABELS} from '../constants/buttons'
import {LABELS} from '../constants/labels'
import {VALUES} from '../constants/values'
import {reportError} from '@github-ui/failbot'
import {CheckIcon, StopIcon, XIcon} from '@primer/octicons-react'
import type {DiscussionCategoryPickerQuery} from '@github-ui/item-picker/DiscussionCategoryPickerQuery.graphql'
import {DiscussionCategories, DiscussionCategoryPicker} from '@github-ui/item-picker/DiscussionCategoryPicker'
import {testIdProps} from '@github-ui/test-id-props'
import {Flash, Box, Octicon, Spinner} from '@primer/react'
import {useNamedColor} from '@github-ui/use-named-color'
import type {ConvertToDiscussionDialogIssuePropertiesQuery} from './__generated__/ConvertToDiscussionDialogIssuePropertiesQuery.graphql'
import type {ConvertToDiscussionDialogIssuePropertiesFragment$key} from './__generated__/ConvertToDiscussionDialogIssuePropertiesFragment.graphql'

export type ConvertToDiscussionDialogProps = {
  issueId: string
  owner: string
  repository: string
  onClose: () => void
}

export const IssueProperties = graphql`
  query ConvertToDiscussionDialogIssuePropertiesQuery($issueId: ID!) {
    node(id: $issueId) {
      ... on Issue {
        ...ConvertToDiscussionDialogIssuePropertiesFragment
      }
    }
  }
`

export const IssuePropertiesFragment = graphql`
  fragment ConvertToDiscussionDialogIssuePropertiesFragment on Issue {
    comments {
      totalCount
    }
    reactions {
      totalCount
    }
    tasklistBlocks {
      totalCount
    }
    assignees {
      totalCount
    }
    projectsV2 {
      totalCount
    }
    projectCards {
      totalCount
    }
    milestone {
      __typename
    }
  }
`

export const ConvertToDiscussionDialog = (props: ConvertToDiscussionDialogProps) => {
  const [categoriesQueryRef, loadDiscussionCategories, disposeDiscussionCategories] =
    useQueryLoader<DiscussionCategoryPickerQuery>(DiscussionCategories)

  const [issuePropertiesQueryRef, loadIssueProperties, disposeIssueProperties] =
    useQueryLoader<ConvertToDiscussionDialogIssuePropertiesQuery>(IssueProperties)

  useEffect(() => {
    loadDiscussionCategories({
      first: VALUES.convertToDiscussionCategoriesFirst,
      owner: props.owner,
      repo: props.repository,
    })

    return () => {
      disposeDiscussionCategories()
    }
  }, [disposeDiscussionCategories, loadDiscussionCategories, props.owner, props.repository])

  useEffect(() => {
    loadIssueProperties({issueId: props.issueId})

    return () => {
      disposeIssueProperties()
    }
  }, [disposeIssueProperties, loadIssueProperties, props.issueId])

  if (!categoriesQueryRef) return null
  if (!issuePropertiesQueryRef) return null

  return (
    <ConvertToDiscussionDialogInternal
      {...props}
      categoriesQueryRef={categoriesQueryRef}
      issuePropertiesQueryRef={issuePropertiesQueryRef}
    />
  )
}

export type ConvertToDiscussionDialogPropsInternal = ConvertToDiscussionDialogProps & {
  categoriesQueryRef: PreloadedQuery<DiscussionCategoryPickerQuery>
  issuePropertiesQueryRef: PreloadedQuery<ConvertToDiscussionDialogIssuePropertiesQuery>
}
export const ConvertToDiscussionDialogInternal = ({
  categoriesQueryRef,
  issuePropertiesQueryRef,
  issueId,
  onClose,
}: ConvertToDiscussionDialogPropsInternal) => {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>()
  const [isConverting, setIsConverting] = useState(false)
  const [showError, setShowError] = useState(false)
  const environment = useRelayEnvironment()
  const issue = usePreloadedQuery<ConvertToDiscussionDialogIssuePropertiesQuery>(
    IssueProperties,
    issuePropertiesQueryRef,
  )

  const handleConvertToDiscussion = useCallback(() => {
    if (!selectedCategoryId || isConverting) return
    setIsConverting(true)

    const input: ConvertIssueToDiscussionInput = {
      categoryId: selectedCategoryId,
      issueId,
    }

    commitConvertIssueToDiscussionMutation({
      environment,
      input,
      onError: (e: Error) => {
        reportError(formatError(e.message))
        setShowError(true)
        setIsConverting(false)
      },
      onCompleted: response => {
        if (response.convertIssueToDiscussion?.discussion) {
          const discussion = response.convertIssueToDiscussion?.discussion
          window.location.href = discussion.url
          return
        } else {
          for (const e of response.convertIssueToDiscussion?.errors || []) {
            reportError(formatError(e.message))
          }
          setShowError(true)
          setIsConverting(false)
        }
      },
    })
  }, [environment, isConverting, issueId, selectedCategoryId])

  const colorGreen = useNamedColor('GREEN').accent

  return (
    <Dialog
      title={LABELS.convertToDiscussion.title}
      onClose={onClose}
      footerButtons={[
        {content: BUTTON_LABELS.cancel, onClick: onClose, disabled: isConverting},
        {
          content: (
            <Box sx={{position: 'relative'}}>
              {isConverting && (
                <Spinner
                  size="small"
                  sx={{position: 'absolute', left: '50%', marginLeft: '-8px', marginTop: '2px'}}
                  aria-label="Loading"
                />
              )}
              <Box sx={{visibility: isConverting ? 'hidden' : 'visible'}}>
                {BUTTON_LABELS.acknowledgeAndConvertToDiscussion}
              </Box>
            </Box>
          ),
          buttonType: isConverting ? 'default' : 'danger',
          onClick: handleConvertToDiscussion,
          disabled: !selectedCategoryId || isConverting,
          ...testIdProps('convertButton'),
        },
      ]}
      width="large"
      renderBody={({children}) => {
        return (
          <>
            {showError && (
              <Flash full={true} variant="danger">
                <Octicon
                  icon={StopIcon}
                  sx={{
                    color: colorGreen,
                  }}
                  className="mr-3"
                />
                {LABELS.somethingWentWrong}
              </Flash>
            )}
            <Box sx={{margin: 3}}>{children}</Box>
          </>
        )
      }}
    >
      {issue?.node && <WhatHappens issue={issue.node} />}
      <p className="mb-1">
        <strong>{LABELS.convertToDiscussion.selectCategoryTitle}</strong>
      </p>
      {categoriesQueryRef && (
        <DiscussionCategoryPicker onSelect={setSelectedCategoryId} categoriesQueryRef={categoriesQueryRef} />
      )}
    </Dialog>
  )
}

function formatError(message: string) {
  return new Error(LABELS.convertToDiscussion.error(message))
}

type WhatHappensProps = {
  issue: ConvertToDiscussionDialogIssuePropertiesFragment$key
}
const WhatHappens = ({issue}: WhatHappensProps) => {
  const {comments, reactions, tasklistBlocks, assignees, projectsV2, projectCards, milestone} = useFragment(
    IssuePropertiesFragment,
    issue,
  )

  const affirmations = useMemo(() => {
    const results = [
      LABELS.convertToDiscussion.affirmations.closedAndLocked,
      LABELS.convertToDiscussion.affirmations.same,
    ]

    if (comments.totalCount > 0 || reactions.totalCount > 0) {
      results.push(LABELS.convertToDiscussion.affirmations.commentsAndReactions)
    }

    return results
  }, [comments, reactions])

  const warnings = useMemo(() => {
    const results = []

    if ((tasklistBlocks?.totalCount || 0) > 0) {
      results.push(LABELS.convertToDiscussion.warnings.taskListBlocks)
    }

    if (assignees.totalCount > 0) {
      results.push(LABELS.convertToDiscussion.warnings.assignees)
    }

    if (projectsV2.totalCount > 0 || projectCards.totalCount > 0) {
      results.push(LABELS.convertToDiscussion.warnings.projects)
    }

    if (milestone) {
      results.push(LABELS.convertToDiscussion.warnings.milestone)
    }

    return results
  }, [tasklistBlocks, assignees, projectsV2, projectCards, milestone])

  return (
    <>
      <p className="mb-3">{LABELS.convertToDiscussion.whatHappens}</p>

      <ul className="list-style-none ml-3 mb-3">
        {affirmations.map((text, index) => (
          <Result key={index} success={true} text={text} />
        ))}
        {warnings.map((text, index) => (
          <Result key={index} success={false} text={text} />
        ))}
      </ul>
    </>
  )
}

type ResultProps = {
  success: boolean
  text: string
}
const Result = ({success, text}: ResultProps) => {
  const colorGreen = useNamedColor('GREEN').accent
  const colorRed = useNamedColor('RED').accent

  return (
    <li className="mb-1">
      <Octicon
        icon={success ? CheckIcon : XIcon}
        sx={{
          color: success ? colorGreen : colorRed,
        }}
        className="mr-1"
      />
      {text}
    </li>
  )
}

try{ ConvertToDiscussionDialog.displayName ||= 'ConvertToDiscussionDialog' } catch {}
try{ ConvertToDiscussionDialogInternal.displayName ||= 'ConvertToDiscussionDialogInternal' } catch {}
try{ WhatHappens.displayName ||= 'WhatHappens' } catch {}
try{ Result.displayName ||= 'Result' } catch {}